import { type CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';

import { type NumberPrecision } from '@blaze-shared/api-contracts-domain';
import { type TableColumnFormat } from '@blaze-shared/elements-domain';
import { digitRegex, regexpMatchLength } from '@blaze-shared/shared-util';

import { type TextInputPropsModel } from './text-input.models';

export function formatPhoneNumber<T>(value: T, phoneCountryCode?: CountryCode): T {
  if (typeof value !== 'string' || regexpMatchLength(digitRegex, value) < 5) {
    return value;
  }

  let phoneNumber;
  try {
    phoneNumber = parsePhoneNumberWithError(value, phoneCountryCode);
    if (phoneCountryCode && phoneNumber.getPossibleCountries().includes(phoneCountryCode)) {
      return phoneNumber.formatNational() as T;
    } else {
      return phoneNumber.formatInternational() as T;
    }
  } catch {
    return value;
  }
}

export function handleFormat<T>(
  value: T,
  opts?: {
    format: TableColumnFormat | undefined;
    precision: NumberPrecision | undefined;
    phoneCountryCode?: CountryCode;
  },
): T | number | '' {
  let result: T | number | '' = value;

  // Percentage should be adjusted
  if (opts?.format === 'percentage' && value !== '') {
    // the +2 is for the 100-s part that's being cut
    result = Number((Number(result) / 100).toFixed((opts?.precision ?? 0) + 2));
  } else if (opts?.format === 'phoneNumber') {
    result = formatPhoneNumber(value, opts?.phoneCountryCode);
  }

  return result;
}

export function getTinyMceToolbarElement(): HTMLElement | null {
  return document.querySelector(
    '.tox.tox-tinymce.tox-tinymce-inline.tox-tinymce--toolbar-sticky-off',
  );
}

export function getPlaceholderText(
  inputProps: TextInputPropsModel['content']['input'] | undefined,
  currentFormat: TableColumnFormat | undefined,
  defaultDuration: string | undefined = undefined,
): string {
  const { placeholder, format, duration } = inputProps ?? {};
  if (placeholder) {
    return placeholder;
  }

  if ((currentFormat ?? format) === 'duration') {
    return defaultDuration ?? duration ?? '';
  }
  if ((currentFormat ?? format) === 'geopoint') {
    return 'Lat°, Long°';
  }

  return '';
}
